import React from "react";
import { FaGraduationCap } from "@react-icons/all-files/fa/FaGraduationCap";
import { FaLinkedin } from "@react-icons/all-files/fa/FaLinkedin";
import { FaEnvelope } from "@react-icons/all-files/fa/FaEnvelope";
import { FaBuilding } from "@react-icons/all-files/fa/FaBuilding";
import { FaBook } from "@react-icons/all-files/fa/FaBook";
import { FaLightbulb } from "@react-icons/all-files/fa/FaLightbulb";
import { FaTimes } from "@react-icons/all-files/fa/FaTimes";
import { FaUserTie } from "@react-icons/all-files/fa/FaUserTie";
import { isRealPhoto } from "./Faculty";

const FacultyProfileModal = ({ isOpen, onClose, faculty }) => {
  if (!isOpen || !faculty) return null;

  const staffName = faculty.Name || faculty.name || "Faculty Member";
  const staffRole = faculty.Role || faculty.Designation || "Faculty";
  const staffDept = faculty.Department || faculty.department || "";
  const staffQual = faculty.Qualification || faculty.HighestQualification || "";
  const staffSpec = faculty.Specialisation || faculty.Specialization || faculty.AreaOfInterest || "";
  const staffResearch = faculty.ResearchPapers || faculty.Publications || faculty.Research || "";
  const staffLinkedin = faculty.Linkedin || faculty.LinkedinProfileLink || "";
  const staffEmail = faculty.Email || faculty.email || "";
  const staffBio = faculty.Bio || faculty.About || "";
  const ProfilePic = faculty.ProfilePic;

  const cleanName = (staffName || "").replace(/^(Dr\.|Mr\.|Ms\.|Prof\.|Col\.|S\.)\s*/i, "").trim();
  const initialLetter = cleanName ? cleanName[0].toUpperCase() : "F";
  const hasPhoto = isRealPhoto(ProfilePic);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-fadeIn"
      onClick={onClose}
    >
      {/* Modal Box */}
      <div
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 transform transition-all my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header Banner */}
        <div className="h-32 bg-gradient-to-r from-[#081C3A] via-[#0f3468] to-[#32727A] relative p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center transition-colors shadow-md z-10"
            title="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Profile Header & Info */}
        <div className="px-6 sm:px-8 pb-6 relative">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5 -mt-16 mb-4">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-gradient-to-tr from-[#081C3A] to-[#32727A] flex items-center justify-center shrink-0">
              {hasPhoto ? (
                <img
                  src={ProfilePic}
                  alt={staffName}
                  className="w-full h-full object-cover object-top rounded-full"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <span className="text-white text-4xl font-bold font-serif uppercase">
                  {initialLetter}
                </span>
              )}
            </div>

            <div className="text-center sm:text-left flex-1">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">
                {staffName}
              </h2>
              <p className="text-sm font-bold text-[#32727a] mt-1">{staffRole}</p>
              {staffDept && (
                <p className="text-xs text-gray-500 flex items-center justify-center sm:justify-start gap-1.5 mt-1">
                  <FaBuilding className="text-gray-400" />
                  <span>{staffDept}</span>
                </p>
              )}
            </div>
          </div>

          {/* Details Sections */}
          <div className="mt-6 space-y-5 border-t border-gray-100 pt-5">
            {/* Qualification & Specialisation */}
            {(staffQual || staffSpec) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-gray-100">
                {staffQual && (
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                      <FaGraduationCap className="text-[#32727a]" /> Highest Qualification
                    </span>
                    <span className="text-sm font-bold text-gray-800 bg-white px-3 py-1 rounded-lg border border-gray-200 inline-block shadow-sm">
                      {staffQual}
                    </span>
                  </div>
                )}

                {staffSpec && (
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                      <FaLightbulb className="text-amber-500" /> Specialisation / Expertise
                    </span>
                    <p className="text-xs font-semibold text-gray-800 leading-relaxed">
                      {staffSpec}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Research Papers & Publications */}
            {staffResearch && (
              <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1.5 mb-2">
                  <FaBook className="text-[#0A66C2]" /> Research Papers &amp; Publications
                </span>
                {Array.isArray(staffResearch) ? (
                  <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                    {staffResearch.map((paper, idx) => (
                      <li key={idx} className="leading-relaxed">
                        {paper}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-800 font-medium leading-relaxed">
                    {staffResearch}
                  </p>
                )}
              </div>
            )}

            {/* Biography / Profile Note */}
            {staffBio && (
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-1">
                  <FaUserTie className="text-[#32727a]" /> Profile Summary
                </span>
                <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-xl border border-gray-100">
                  {staffBio}
                </p>
              </div>
            )}

            {/* Connect Buttons */}
            <div className="pt-3 flex flex-wrap items-center gap-3 justify-end border-t border-gray-100">
              {staffLinkedin && (
                <a
                  href={staffLinkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#0A66C2] hover:bg-[#084e96] px-4 py-2 rounded-xl transition-all shadow-md hover:shadow-lg"
                >
                  <FaLinkedin className="w-4 h-4" />
                  <span>LinkedIn Profile</span>
                </a>
              )}

              {staffEmail && (
                <a
                  href={`mailto:${staffEmail}`}
                  className="inline-flex items-center gap-2 text-xs font-bold text-gray-800 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl transition-all border border-gray-300"
                >
                  <FaEnvelope className="w-3.5 h-3.5 text-gray-600" />
                  <span>Send Email</span>
                </a>
              )}

              <button
                onClick={onClose}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacultyProfileModal;
